import { AnimatePresence, motion } from 'motion/react'
import { type PropsWithChildren, useEffect } from 'react'
import { cn } from '@/src/utils/styles'

interface BottomSheetProps {
  isOpen?: boolean
  setIsOpen?: (isOpen: boolean) => void
  scrimClassName?: string
  variant?: 'bottom' | 'centered-dropdown'
  containerClassName?: string
}

export const BottomSheet = function BottomSheet({
  children,
  isOpen,
  setIsOpen,
  scrimClassName,
  variant = 'bottom',
  containerClassName,
}: PropsWithChildren<BottomSheetProps>) {
  // Add Escape key handler
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen?.(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, setIsOpen])

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen?.(false)}
            className={cn(
              variant === 'bottom'
                ? 'bg-scrim absolute inset-0 z-20 rounded-2xl'
                : 'bg-scrim-nonmodal fixed inset-0 z-40',
              scrimClassName,
            )}
          />
        )}
      </AnimatePresence>
      <AnimatePresence mode='popLayout'>
        {isOpen && (
          <motion.div
            initial={
              variant === 'centered-dropdown'
                ? { opacity: 0, y: -10 }
                : { opacity: 0, y: 100 }
            }
            animate={
              variant === 'centered-dropdown'
                ? { opacity: 1, y: 0 }
                : { opacity: 1, y: 0 }
            }
            exit={
              variant === 'centered-dropdown'
                ? { opacity: 0, y: -10 }
                : { opacity: 0, y: 100 }
            }
            transition={{ duration: 0.3, type: 'spring' }}
            className={cn(
              variant === 'bottom'
                ? 'absolute bottom-0 max-h-[calc(100%-2rem)] z-30 py-5 flex flex-col bg-surface2-100 w-full rounded-t-3xl gap-5'
                : 'absolute top-full mt-4 left-1/2 -translate-x-1/2 z-50 flex flex-col bg-foreground w-64 rounded-2xl border border-border1 shadow-2xl py-2',
              containerClassName,
            )}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
